import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useInitiateBankPaymentMutation } from "@/services/bankPaymentApi";
import { toast } from "@/components/ui/use-toast";
import { ErrorType, BankPaymentError, BankPaymentSession, ForeePaymentToken, ForeePaymentError, ForeeCallbackResponse, BankPaymentInitiateRequest } from "@/types";
import useBankCheckout from "@/hooks/useBankCheckout";
import useForeeCheckout from "@/hooks/useForeeCheckout";
import BankTransactionErrorCard from "@/components/ui/bankPayment/BankTransactionErrorCard";
import BankPaymentLoader from "@/components/ui/bankPayment/BankPaymentLoader";
import { decodeJwtToken } from "@/lib/helper";
// Mobile detection for production environment
const env = (import.meta as any).env?.VITE_ENV || "development";
const allowDesktop = env !== "production";

type ViewState =
    | { kind: "idle" }
    | { kind: "initiating" }
    | { kind: "error"; error: BankPaymentError | ForeePaymentError };

type PaymentType = "bank" | "foree";

const PaymentGateway = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = (searchParams.get("t") || "").trim();
    const [view, setView] = useState<ViewState>({ kind: "idle" });
    const [paymentType, setPaymentType] = useState<PaymentType | null>(null);
    const [session, setSession] = useState<BankPaymentSession | null>(null);
    const initiatedTokenRef = useRef<string | null>(null);
    const sessionTimestampRef = useRef<number | null>(null);
    const foreeInitiateCheckoutRef = useRef<(() => void) | null>(null);

    // env gate: allow desktop testing unless explicitly production

    const isMobile = useMemo(() => {
        const ua = navigator.userAgent || "";
        const uaMobile =
            /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        const smallViewport = window.innerWidth < 768;
        return uaMobile || smallViewport;
    }, []);

    // If not mobile and not allowed by env, show NotFound
    if (!isMobile && !allowDesktop) {
        return (
            <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", padding: 16 }}>
                <div style={{ maxWidth: 420, textAlign: "center" }}>
                    <h2 style={{ marginBottom: 12 }}>Payment Gateway</h2>
                    <p style={{ marginBottom: 24 }}>This page is only available on mobile in production.</p>
                </div>
            </div>
        );
    }

    // Detect payment type from token
    const detectedPaymentType = useMemo((): PaymentType | null => {
        if (!token) return null;
        try {
            const decoded = decodeJwtToken(token) as BankPaymentInitiateRequest;
            
            // Check if it's a Foree payment token
            if ('payment_method' in decoded && decoded.payment_method === 'foree') {
                return 'foree';
            }
            
            // Check if it's a bank payment (Alfalah) by looking for payment_method
            if ('payment_method' in decoded && decoded.payment_method === 'alfalah') {
                return 'bank';
            }
            
            // Default to bank payment for backward compatibility
            return 'bank';
        } catch (error) {
            console.error('Error decoding token:', error);
            return 'bank'; // Default to bank payment
        }
    }, [token]);

    // Update payment type when detected
    useEffect(() => {
        if (detectedPaymentType) {
            setPaymentType(detectedPaymentType);
        }
    }, [detectedPaymentType]);

    const [initiateBankPayment, { isLoading: isInitiating, isError, error }] = useInitiateBankPaymentMutation();

    // Memoize the initiate function to prevent unnecessary re-renders
    const stableInitiateBankPayment = useCallback(
        (params: BankPaymentInitiateRequest) => {
            console.log('🔧 stableInitiateBankPayment called with params:', params);
            // Use the params passed to the function, not decode token again
            return initiateBankPayment(params).unwrap();
        },
        [initiateBankPayment]
    );

    // Foree checkout hook
    const foreeCheckout = useForeeCheckout({
        token,
        onSuccess: (response: ForeeCallbackResponse) => {
            // navigate("/payment/success", {
            //     state: {
            //         transaction_id: response.transaction_ref_id || response.reference_number,
            //         message: response.message || "Payment processed successfully",
            //         foree_response: response
            //     }
            // });
            navigate("/payment/process");
        },
        onError: (errorMessage: string) => {
            setView({
                kind: "error",
                error: {
                    error: true,
                    title: "Foree Payment Error",
                    message: errorMessage,
                },
            });
        },
        onReady: () => {
            // Foree is ready, checkout will be initiated automatically
        }
    });

    // Store the initiate function in a ref to avoid effect re-runs
    useEffect(() => {
        foreeInitiateCheckoutRef.current = foreeCheckout.initiateCheckout;
    }, [foreeCheckout.initiateCheckout]);

    // Initiate checkout session when token is present
    useEffect(() => {
        // Only process if we have a token and haven't already initiated this token
        if (!token || !paymentType) {
            console.log('❌ Missing token or paymentType:', { token: !!token, paymentType });
            return;
        }

        // Prevent duplicate initiation for the same token (e.g., StrictMode double-invoke)
        if (initiatedTokenRef.current === token) {
            console.log('⚠️ Token already initiated, skipping:', token);
            return;
        }

        console.log('🚀 Starting payment initiation for:', paymentType);
        initiatedTokenRef.current = token;
        setView({ kind: "initiating" });

        const initiatePayment = async () => {
            try {
                if (paymentType === 'bank') {
                    // Handle bank payment
                    const payload = decodeJwtToken(token) as BankPaymentInitiateRequest;
                    console.log('📋 Bank payment payload:', payload);
                    
                    console.log('📞 Calling stableInitiateBankPayment...');
                    const result = await stableInitiateBankPayment(payload);
                    console.log('✅ Bank payment API result:', result);

                    // Check if this token is still the current one (not cancelled by a new token)
                    if (initiatedTokenRef.current !== token) {
                        console.log('⚠️ Token changed during API call, ignoring result');
                        return;
                    }
                    console.log('✅ Token still current, proceeding with result processing');

                    // Extract session from API response - cast to any to handle actual API structure
                    const resultAny = result as any;
                    const apiSession = resultAny?.data?.session;
                    console.log('Raw API session:', apiSession);
                    
                    if (!apiSession?.id) {
                        console.error('No valid session ID found in API response:', result);
                        setView({
                            kind: "error",
                            error: {
                                error: true,
                                title: "Bank Payment",
                                message: "Could not start payment session. Please try again later.",
                            },
                        });
                    } else {
                        // Create a proper BankPaymentSession object with the required structure
                        // The API returns a simple session object, but our type requires more fields
                        const session: BankPaymentSession = {
                            id: apiSession.id,
                            merchant: {
                                name: resultAny?.data?.merchant || "TESTATMOSPHERGYM",
                                url: ""
                            },
                            order: {
                                amount: payload.amount.toString(),
                                currency: "PKR",
                                id: payload.invoice_id.toString(),
                                description: `Payment for ${payload.item_type} #${payload.item_id}`
                            }
                        };
                        
                        console.log('✅ Created BankPaymentSession object for checkout:', session);
                        console.log('🔄 Setting session state - this will trigger useBankCheckout hook');
                        
                        setSession(session);
                        sessionTimestampRef.current = Date.now();
                        setView({ kind: "idle" });
                        
                        console.log('✅ Session state set, Bank Alfalah checkout should initialize');
                    }
                } else if (paymentType === 'foree') {
                    // Foree payment - wait for hook to be ready, then initiate
                    // Check if this token is still the current one
                    if (initiatedTokenRef.current !== token) {
                        console.log('⚠️ Token changed during Foree setup, ignoring');
                        return;
                    }
                    
                    // If Foree is ready, initiate checkout
                    if (foreeCheckout.isReady && foreeInitiateCheckoutRef.current) {
                        foreeInitiateCheckoutRef.current();
                        setView({ kind: "idle" });
                    } else {
                        // Wait for Foree to be ready
                        setView({ kind: "initiating" });
                    }
                }
            } catch (err: any) {
                // Check if this token is still the current one
                if (initiatedTokenRef.current !== token) {
                    console.log('⚠️ Token changed during error handling, ignoring');
                    return;
                }
                console.error('❌ Payment initiation failed:', err);
                console.error('❌ Error details:', {
                    message: err?.message,
                    data: err?.data,
                    status: err?.status,
                    stack: err?.stack
                });
                
                const errorData = (err?.data as BankPaymentError) || {
                    error: true,
                    title: "Error",
                    message: "Error initializing checkout. Please try again.",
                };
                setView({ kind: "error", error: errorData });
            }
        };

        void initiatePayment();
    }, [token, paymentType, stableInitiateBankPayment, foreeCheckout.isReady]);

    // Auto-initiate Foree checkout when it becomes ready
    useEffect(() => {
        if (paymentType === 'foree' && 
            foreeCheckout.isReady && 
            initiatedTokenRef.current === token &&
            view.kind === "initiating" &&
            foreeInitiateCheckoutRef.current) {
            foreeInitiateCheckoutRef.current();
            setView({ kind: "idle" });
        }
    }, [paymentType, foreeCheckout.isReady, token, view.kind]);

    // Monitor session state changes
    useEffect(() => {
        console.log('🔍 Session state changed:', { 
            hasSession: !!session, 
            sessionId: session?.id
        });
    }, [session]);

    // Initialize and show checkout when session is ready
    useBankCheckout({
        session,
        onError: (msg) => {
            // Reset session if it's expired to allow retry
            if (msg.includes("expired") || msg.includes("not found")) {
                setSession(null);
                initiatedTokenRef.current = null;
                sessionTimestampRef.current = null;
            }
            setView({ kind: "error", error: { error: true, title: "Payment Error", message: msg } });
        },
    });

    // Check for session timeout (15 minutes)
    useEffect(() => {
        if (!session || !sessionTimestampRef.current) return;

        const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
        const timeoutId = setTimeout(() => {
            const elapsed = Date.now() - sessionTimestampRef.current!;
            if (elapsed >= SESSION_TIMEOUT) {
                setSession(null);
                initiatedTokenRef.current = null;
                sessionTimestampRef.current = null;
                setView({
                    kind: "error",
                    error: {
                        error: true,
                        title: "Session Expired",
                        message: "Your payment session has expired. Please refresh the page to try again.",
                    },
                });
            }
        }, SESSION_TIMEOUT);

        return () => clearTimeout(timeoutId);
    }, [session]);

    // UI states
    console.log('🎨 Rendering PaymentGateway - Current state:', {
        isInitiating,
        viewKind: view.kind,
        paymentType,
        hasSession: !!session,
        sessionId: session?.id,
        foreeLoading: paymentType === 'foree' ? foreeCheckout.isLoadingMember : 'N/A',
        foreeReady: paymentType === 'foree' ? foreeCheckout.isReady : 'N/A'
    });
    
    // Loading state for initiating or while showing checkout
    // Note: Don't show loader when session exists for bank payments - let the checkout display
    if (isInitiating || view.kind === "initiating" || (paymentType === 'foree' && (foreeCheckout.isLoadingMember || !foreeCheckout.isReady))) {
        console.log('🔄 Showing BankPaymentLoader');
        return <BankPaymentLoader />;
    }

    // Error states
    if (view.kind === "error" || isError || (!token && view.kind === "idle")) {
        const typedError = error as ErrorType;
        let errorMessage: string;
        let errorTitle = "Payment Gateway Error";
        
        if (!token && view.kind === "idle") {
            errorMessage = "Missing payment token. Please open this link from the app.";
            errorTitle = "Invalid Request";
        } else if (view.kind === "error") {
            errorMessage = view.error.message;
            errorTitle = view.error.title;
        } else {
            errorMessage = typedError?.data?.detail || typedError?.data?.message || "Failed to initiate payment checkout.";
        }

        // Show toast for API errors
        if (error && typeof error === "object" && "data" in error) {
            toast({ 
                variant: "destructive", 
                title: errorTitle,
                description: errorMessage 
            });
        }

        console.log('❌ Showing error card:', errorTitle, errorMessage);
        return (
            <BankTransactionErrorCard 
                title={errorTitle} 
                message={errorMessage}
                onRetry={() => {
                    // Reset state and retry
                    setView({ kind: "idle" });
                    initiatedTokenRef.current = null;
                    sessionTimestampRef.current = null;
                    setSession(null);
                }}
            />
        );
    }

    // Idle state (waiting for checkout to be shown or allowing checkout to display)
    console.log('💤 Showing idle state - Bank Alfalah checkout should be initializing in background');
    return (
        <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", padding: 16 }}>
            <div style={{ textAlign: "center" }}>
                <h2 style={{ marginBottom: 16 }}>Initializing Payment</h2>
                <p>Please wait while we prepare your secure payment checkout...</p>         
            </div>
        </div>
    );
};

export default PaymentGateway;

